import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Tooltip from '@amzn/meridian/tooltip';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import HoverZoomImage from './HoverZoomImage';
import Icon from '@amzn/meridian/icon';
import closeKnockoutTokens from '@amzn/meridian-tokens/base/icon/close-knockout';
import { ItemEntry } from '@graphql/generated/graphql';
import { storeSubcategory } from '@components/agile/types/AgileMacroplanogramTypes';
import { MacroplanogramScope } from 'src/types/programConfig';
import { timestampToDateStr, isIndefinite, isActiveEntry } from '@components/agile/utils/dateHelpers';
import useApplyItemsChange from '@store/hooks/useApplyItemsChange';
import { useProgramConfig } from 'src/hooks/useProgramConfig';

export interface DeleteItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeleteSuccess: () => void;
  storeId: string;
  macroplanogramId: string;
  selectedEntries: ItemEntry[];
  subcategories: storeSubcategory[];
}

const DeleteItemsModal = (props: DeleteItemsModalProps) => {
  const { isOpen, onClose, onDeleteSuccess, storeId, macroplanogramId, selectedEntries, subcategories } = props;
  const config = useProgramConfig();
  const itemIdentifierLabel = config.labels.itemIdentifierType;
  const subcategoryLabel = config.labels.subcategory;
  const categoryLabel = config.labels.category;
  const scope: MacroplanogramScope = config.features.itemAssortment?.scope ?? 'subcategory';
  const scopeColumnLabel = scope === 'subcategory' ? subcategoryLabel : categoryLabel;
  const [entries, setEntries] = useState<ItemEntry[]>([]);
  const [deleteErrors, setDeleteErrors] = useState<Map<string, string>>(new Map());
  const [globalError, setGlobalError] = useState<string | null>(null);
  const { mutateAsync, isLoading: isDeleting } = useApplyItemsChange();
  const subcategoryMap = useMemo(
    () => new Map(subcategories.map((s) => [s.subcategoryId, s.subcategoryName])),
    [subcategories],
  );

  // Initialize entries only on modal open transition (false → true)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevOpenRef.current && selectedEntries.length > 0) {
      setEntries(selectedEntries);
      setDeleteErrors(new Map());
      setGlobalError(null);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, selectedEntries]);

  // Inline validation: flag active entries that cannot be deleted
  const activeEntryErrors = useMemo(() => {
    const errors = new Map<string, string>();
    entries.forEach((entry) => {
      if (isActiveEntry(Number(entry.startTime))) {
        errors.set(entry.entryId, 'Cannot delete active item. Only future entries can be deleted.');
      }
    });
    return errors;
  }, [entries]);

  // Merge inline validation errors with server-side errors (server uses itemId, inline uses entryId)
  const allErrors = useMemo(() => {
    const merged = new Map(activeEntryErrors);
    // Server errors are keyed by itemId — map them to all matching entryIds
    deleteErrors.forEach((msg, itemId) => {
      entries.filter((e) => e.itemId === itemId).forEach((e) => merged.set(e.entryId, msg));
    });
    return merged;
  }, [activeEntryErrors, deleteErrors, entries]);

  const hasActiveEntries = activeEntryErrors.size > 0;

  const handleClose = useCallback(() => {
    setEntries([]);
    setDeleteErrors(new Map());
    setGlobalError(null);
    onClose();
  }, [onClose]);

  const handleRemoveEntry = useCallback(
    (entryId: string) => {
      setEntries((prev) => prev.filter((e) => e.entryId !== entryId));
      setDeleteErrors((prev) => {
        const next = new Map(prev);
        // Clear any error for the removed item
        const removed = entries.find((e) => e.entryId === entryId);
        if (removed) next.delete(removed.itemId);
        return next;
      });
    },
    [entries],
  );

  const handleDelete = useCallback(async () => {
    try {
      setGlobalError(null);
      setDeleteErrors(new Map());
      const result = await mutateAsync({
        input: {
          storeId,
          macroplanogramId,
          deleteEntryIds: entries.map((e) => e.entryId),
        },
      });
      const errors = result?.applyItemsChange?.errors ?? [];
      if (errors.length > 0) {
        const itemErrors = new Map<string, string>();
        const internalErrors: string[] = [];
        const entryItemIds = new Set(entries.map((e) => e.itemId));
        errors.forEach((e) => {
          if (entryItemIds.has(e.key)) {
            itemErrors.set(e.key, e.error.errorMessage);
          } else {
            internalErrors.push(`${e.key}: ${e.error.errorMessage}`);
          }
        });
        if (itemErrors.size > 0) {
          setDeleteErrors(itemErrors);
        }
        if (internalErrors.length > 0) {
          setGlobalError(internalErrors.join('; '));
        }
      } else {
        onDeleteSuccess();
        handleClose();
      }
    } catch (err) {
      setGlobalError(err instanceof Error ? err.message : 'An unexpected error occurred while deleting items.');
    }
  }, [mutateAsync, storeId, macroplanogramId, entries, onDeleteSuccess, handleClose]);

  return (
    <Modal title="Delete Items" open={isOpen} onClose={handleClose} scrollContainer="modal" width="900px">
      <Column spacing="400">
        {globalError && (
          <Alert type="error" size="medium">
            {globalError}
          </Alert>
        )}

        {entries.length === 0 ? (
          <Text type="b300">No items to delete.</Text>
        ) : (
          <>
            <Text>{`The following ${entries.length} item(s) will be deleted:`}</Text>
            <Table headerRows={1} showDividers spacing="small">
              <TableRow backgroundColor="secondary">
                <TableCell>{itemIdentifierLabel}</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Title</TableCell>
                {scope === 'subcategory' && <TableCell>{scopeColumnLabel}</TableCell>}
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Remove</TableCell>
              </TableRow>
              {entries.map((entry) => (
                <TableRow key={entry.entryId}>
                  <TableCell>
                    <Column>
                      <Text type="b200">{entry.itemId}</Text>
                      {allErrors.has(entry.entryId) && (
                        <Text type="b100" color="error">
                          {allErrors.get(entry.entryId)}
                        </Text>
                      )}
                    </Column>
                  </TableCell>
                  <TableCell>
                    {entry.itemMetaDataEntry?.image ? (
                      <HoverZoomImage src={entry.itemMetaDataEntry.image} alt={entry.itemId} width={40} height={40} />
                    ) : (
                      <Text type="b200">—</Text>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box maxWidth="200px">
                      <Text type="b200">{entry.itemMetaDataEntry?.title ?? '—'}</Text>
                    </Box>
                  </TableCell>
                  {scope === 'subcategory' && (
                    <TableCell>
                      <Text type="b200">
                        {entry.subcategoryId ? (subcategoryMap.get(entry.subcategoryId) ?? '') : ''}
                      </Text>
                    </TableCell>
                  )}
                  <TableCell>
                    <Text type="b200">{timestampToDateStr(Number(entry.startTime))}</Text>
                  </TableCell>
                  <TableCell>
                    <Text type="b200">
                      {isIndefinite(Number(entry.endTime)) ? 'Indefinite' : timestampToDateStr(Number(entry.endTime))}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Button
                      type="icon"
                      onClick={() => handleRemoveEntry(entry.entryId)}
                      aria-label={`Remove ${entry.itemId}`}
                    >
                      <Icon tokens={closeKnockoutTokens} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          </>
        )}
      </Column>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="secondary" onClick={handleClose} disabled={isDeleting}>
            Cancel
          </Button>
          {entries.length === 0 ? (
            <Tooltip position="top" title="No items to delete">
              <span tabIndex={0}>
                <Button type="primary" disabled>
                  Delete
                </Button>
              </span>
            </Tooltip>
          ) : hasActiveEntries ? (
            <Tooltip position="top" title="Remove active items before deleting">
              <span tabIndex={0}>
                <Button type="primary" disabled>
                  Delete
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button type="primary" disabled={isDeleting} onClick={handleDelete}>
              {isDeleting ? <Loader size="small" /> : 'Delete'}
            </Button>
          )}
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default DeleteItemsModal;
